/**
 * FAQ Search and Filter — v2 (native <details> + TreeWalker highlight)
 *
 * Rewrite rationale (2026-04):
 *  - Old version used innerHTML replacement for highlighting, which destroyed
 *    the click listener attached to each question. After any search + clear
 *    cycle, questions became un-clickable. New version walks text nodes only.
 *  - Works against the new <details>/<summary>/.dh-faq__panel markup, with
 *    legacy selector fallbacks so it keeps working during a staged rollout.
 *  - Auto-expands matching items on search; restores prior open/closed state
 *    when the query clears.
 *  - 150ms input debounce to keep 112-question re-renders smooth.
 *  - Adds URL-hash deep-linking: /pages/faq#faq-xxx opens that item on load.
 */

(function () {
  'use strict';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    var searchInput = document.getElementById('faq-search-input');
    var searchClear = document.getElementById('faq-search-clear');
    var resultsCount = document.getElementById('faq-search-results-count');
    var resultsText = document.getElementById('faq-results-text');
    var filterButtons = document.querySelectorAll('.faq-filter-btn');

    // New markup first; fall back to legacy selectors for safety.
    var itemSelector = '.dh-faq__item, .faq-item';
    var categorySelector = '.dh-faq__category, .faq-category-section';
    var questionSelector = '.dh-faq__q, .faq-question';
    var panelSelector = '.dh-faq__panel-inner, .dh-faq__answer, .faq-answer';

    var items = Array.prototype.slice.call(document.querySelectorAll(itemSelector));
    var categories = Array.prototype.slice.call(document.querySelectorAll(categorySelector));

    if (!searchInput || !items.length) return;

    var currentCategory = 'all';
    var searchTerm = '';
    var priorOpenState = null; // remembers which <details> were open before search forced them open

    // Debounced handler
    var debounceTimer;
    searchInput.addEventListener('input', function (e) {
      var value = e.target.value;
      searchClear.style.display = value.length > 0 ? 'flex' : 'none';
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        searchTerm = value.toLowerCase().trim();
        applyFilter();
      }, 150);
    });

    if (searchClear) {
      searchClear.addEventListener('click', function () {
        searchInput.value = '';
        searchTerm = '';
        searchClear.style.display = 'none';
        applyFilter();
        searchInput.focus();
      });
    }

    filterButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        filterButtons.forEach(function (b) { b.classList.remove('active'); });
        this.classList.add('active');
        currentCategory = this.dataset.category || 'all';
        applyFilter();
      });
    });

    function applyFilter() {
      var visibleCount = 0;
      var totalCount = 0;
      var categoryVisibility = {};
      var isSearching = searchTerm !== '';

      // On search start, snapshot the user's current open/closed state so we
      // can restore it when they clear the query.
      if (isSearching && priorOpenState === null) {
        priorOpenState = new Map();
        items.forEach(function (item) {
          if (item.tagName === 'DETAILS') {
            priorOpenState.set(item, item.open);
          }
        });
      }

      var firstMatchOnlyInAnswer = null;

      items.forEach(function (item) {
        var itemCategory = item.dataset.category || 'all';
        var question = item.querySelector(questionSelector);
        var panel = item.querySelector(panelSelector);
        var questionText = question ? (question.textContent || '').toLowerCase() : '';
        var answerText = panel ? (panel.textContent || '').toLowerCase() : '';

        totalCount++;

        var categoryMatch = currentCategory === 'all' || itemCategory === currentCategory;
        var matchInQuestion = searchTerm !== '' && questionText.indexOf(searchTerm) !== -1;
        var matchInAnswer = searchTerm !== '' && !matchInQuestion && answerText.indexOf(searchTerm) !== -1;
        var searchMatch = searchTerm === '' || matchInQuestion || matchInAnswer;

        if (categoryMatch && searchMatch) {
          item.classList.remove('faq-hidden');
          visibleCount++;
          categoryVisibility[itemCategory] = true;

          if (isSearching) {
            highlight(item, searchTerm);
            // Do NOT auto-open anything on search — highlighting is enough
            // signal, and auto-opening was causing the page to jump around
            // while typing. User clicks to expand.
            if (item.tagName === 'DETAILS') {
              var prior = priorOpenState && priorOpenState.get(item);
              item.open = prior === true;
            }
          } else {
            unhighlight(item);
          }
        } else {
          item.classList.add('faq-hidden');
          unhighlight(item);
        }
      });

      // Do NOT auto-scroll on search. Filtering + highlights are enough visual
      // feedback. Auto-scrolling on every keystroke was throwing users to the
      // bottom of the now-short page after non-matches above had been hidden.

      // Restore user's prior open state when search clears
      if (!isSearching && priorOpenState) {
        priorOpenState.forEach(function (wasOpen, el) {
          if (el.tagName === 'DETAILS') el.open = wasOpen;
        });
        priorOpenState = null;
      }

      // Show/hide category dividers based on whether any of their items are visible
      categories.forEach(function (cat) {
        var catKey = cat.dataset.category || 'all';
        var hasVisible = categoryVisibility[catKey];
        if (currentCategory === 'all') {
          cat.classList.toggle('faq-hidden', !hasVisible);
        } else {
          cat.classList.toggle('faq-hidden', !(catKey === currentCategory && hasVisible));
        }
      });

      updateResultsCount(visibleCount, totalCount);
      showNoResultsMessage(visibleCount);
    }

    // --- Highlighting via TreeWalker (non-destructive) ---

    function highlight(item, term) {
      unhighlight(item); // clear any previous highlights first
      if (!term) return;
      var question = item.querySelector(questionSelector);
      var panel = item.querySelector(panelSelector);
      if (question) walkAndWrap(question, term);
      if (panel) walkAndWrap(panel, term);
    }

    function unhighlight(item) {
      var marks = item.querySelectorAll('mark.faq-highlight');
      marks.forEach(function (mark) {
        var parent = mark.parentNode;
        if (!parent) return;
        // Replace <mark>text</mark> with a plain text node
        parent.replaceChild(document.createTextNode(mark.textContent), mark);
        parent.normalize();
      });
    }

    function walkAndWrap(root, term) {
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: function (node) {
          if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          // Skip text inside <script>, <style>, or existing <mark>
          var p = node.parentNode;
          if (!p) return NodeFilter.FILTER_REJECT;
          var tag = p.nodeName;
          if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'MARK') return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      });

      var toWrap = [];
      while (walker.nextNode()) toWrap.push(walker.currentNode);

      var re = new RegExp(escapeRegex(term), 'gi');
      toWrap.forEach(function (textNode) {
        var text = textNode.nodeValue;
        if (text.toLowerCase().indexOf(term) === -1) return;
        var frag = document.createDocumentFragment();
        var lastIndex = 0;
        var match;
        re.lastIndex = 0;
        while ((match = re.exec(text)) !== null) {
          if (match.index > lastIndex) {
            frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
          }
          var mark = document.createElement('mark');
          mark.className = 'faq-highlight';
          mark.textContent = match[0];
          frag.appendChild(mark);
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex)));
        }
        textNode.parentNode.replaceChild(frag, textNode);
      });
    }

    function escapeRegex(s) {
      return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    function updateResultsCount(visible, total) {
      if (!resultsCount || !resultsText) return;
      if (searchTerm !== '' || currentCategory !== 'all') {
        resultsCount.style.display = 'block';
        if (searchTerm !== '') {
          resultsText.textContent = 'Showing ' + visible + ' of ' + total + ' questions matching "' + searchTerm + '"';
        } else {
          resultsText.textContent = 'Showing ' + visible + ' questions in this category';
        }
      } else {
        resultsCount.style.display = 'none';
      }
    }

    function showNoResultsMessage(visibleCount) {
      var noResultsDiv = document.getElementById('faq-no-results');
      if (visibleCount === 0 && (searchTerm !== '' || currentCategory !== 'all')) {
        if (!noResultsDiv) {
          noResultsDiv = document.createElement('div');
          noResultsDiv.id = 'faq-no-results';
          noResultsDiv.className = 'faq-no-results';
          var container = document.querySelector('.dh-faq__list') ||
                          document.querySelector('.faq-container') ||
                          document.querySelector('.dh-faq');
          if (container) container.appendChild(noResultsDiv);
        }
        var message = '<h3>No results found</h3>';
        if (searchTerm !== '') {
          message += '<p>We couldn\'t find any questions matching "<strong>' + escapeHtml(searchTerm) + '</strong>".</p>';
          message += '<p>Try different keywords or browse all topics.</p>';
        } else {
          message += '<p>No questions found in this category.</p>';
        }
        message += '<p>Still have questions? <a href="/pages/contact">Contact us</a> at <a href="tel:+18002223901">800-222-3901</a> or <a href="mailto:info@desertharvest.com">info@desertharvest.com</a></p>';
        noResultsDiv.innerHTML = message;
        noResultsDiv.style.display = 'block';
      } else if (noResultsDiv) {
        noResultsDiv.style.display = 'none';
      }
    }

    function escapeHtml(s) {
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    // Initial render
    applyFilter();

    // -----------------------------------------------------------------
    // AI SEARCH LAYER (Railway + DeepSeek V3.2)
    // Natural-language queries get a synthesised FDA-compliant summary
    // above the FAQ list, plus product link chips and a feedback widget.
    // Falls back silently if the endpoint is unreachable — the literal
    // filter above always still works.
    // -----------------------------------------------------------------
    var aiEndpoint = (window.DH_FAQ_AI_ENDPOINT || 'https://faq-ai-search-production.up.railway.app').replace(/\/$/, '');
    var aiContainer = document.getElementById('dh-faq-ai-card-mount') || createAiMount();
    var aiDebounce;
    var aiInflight = null;
    var lastAiQuery = '';

    function createAiMount() {
      var mount = document.createElement('div');
      mount.id = 'dh-faq-ai-card-mount';
      mount.setAttribute('aria-live', 'polite');
      var anchor = document.querySelector('.faq-search-wrapper');
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(mount, anchor.nextSibling);
      } else {
        document.body.appendChild(mount);
      }
      return mount;
    }

    // Heuristic: only fire the AI when the question looks "finished".
    // Data from real traffic shows users typing slowly — if we fire on
    // every pause ≥700ms we end up calling the model 9x for one question
    // and logging 9 "fallback" rows. Require a real-looking question.
    function shouldAskAI(q) {
      if (!q) return false;
      // Question mark is always a clear finisher (any length ≥ 4).
      if (q.length >= 4 && q.indexOf('?') !== -1) return true;
      // Otherwise require 20+ chars AND 4+ words — tighter than before
      // to reduce partial-typing waste (data showed "Does this capsule"
      // triggering fallbacks at 15/3).
      if (q.length < 20) return false;
      var words = q.split(/\s+/).filter(Boolean);
      return words.length >= 4;
    }

    searchInput.addEventListener('input', function (e) {
      var value = (e.target.value || '').trim();
      clearTimeout(aiDebounce);
      // Abort any in-flight request as soon as user starts typing again.
      if (aiInflight && typeof aiInflight.abort === 'function') {
        try { aiInflight.abort(); } catch (e) {}
        aiInflight = null;
      }
      if (!shouldAskAI(value)) { clearAiCard(); return; }
      if (value === lastAiQuery) return;
      // 1400ms gives users time to finish typing. Cuts duplicate calls.
      aiDebounce = setTimeout(function () { askAI(value); }, 1400);
    });

    if (searchClear) {
      searchClear.addEventListener('click', function () { clearAiCard(); lastAiQuery = ''; });
    }

    function askAI(query) {
      lastAiQuery = query;
      renderSkeleton();
      if (aiInflight && typeof aiInflight.abort === 'function') aiInflight.abort();
      var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      aiInflight = controller;
      var started = Date.now();
      fetch(aiEndpoint + '/api/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: query,
          page_url: window.location.pathname,
          scope: 'faq',
          max_matches: 5
        }),
        signal: controller ? controller.signal : undefined,
        credentials: 'omit'
      })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          if (query !== lastAiQuery) return; // user typed something new
          renderAnswer(data, Date.now() - started);
          pullMatchedToTop(data.matched || []);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          clearAiCard(); // silent fallback — literal filter is already doing its job
        });
    }

    function clearAiCard() {
      aiContainer.innerHTML = '';
    }

    function renderSkeleton() {
      aiContainer.innerHTML =
        '<div class="dh-faq__ai-card dh-faq__ai-card--loading" role="region" aria-label="Searching FAQs">' +
          '<div class="dh-faq__ai-header">' +
            '<span class="dh-faq__ai-badge">Searching</span>' +
            '<span class="dh-faq__ai-muted">reading 99 FAQs\u2026</span>' +
          '</div>' +
          '<div class="dh-faq__ai-skeleton dh-faq__ai-skeleton--1"></div>' +
          '<div class="dh-faq__ai-skeleton dh-faq__ai-skeleton--2"></div>' +
          '<div class="dh-faq__ai-skeleton dh-faq__ai-skeleton--3"></div>' +
        '</div>';
    }

    function renderAnswer(data, latencyMs) {
      // --- Two-tier response system ---
      // Tier 1 "verified": FAQ directly answers the question — show real FAQ body
      // Tier 2 "ai_suggested": AI synthesis — show summary with accuracy caveat
      var isVerified = data.tier === 'verified';
      var primaryBody = isVerified && data.matched && data.matched[0] && data.matched[0].body;

      // Badge + card class per tier
      var badgeText = isVerified ? 'From Our FAQ' : 'AI Suggested';
      var badgeClass = isVerified ? 'dh-faq__ai-badge dh-faq__ai-badge--verified' : 'dh-faq__ai-badge dh-faq__ai-badge--ai';
      var cardClass = isVerified ? 'dh-faq__ai-card dh-faq__ai-card--verified' : 'dh-faq__ai-card dh-faq__ai-card--ai';

      // Summary content: Tier 1 shows FAQ body, Tier 2 shows AI summary
      var summaryContent = primaryBody || data.summary || '';

      // AI caveat (Tier 2 only)
      var aiCaveat = !isVerified
        ? '<div class="dh-faq__ai-caveat">This answer was generated by AI based on our FAQ content. For definitive answers, contact us at <a href="tel:+18002223901" class="dh-faq__ai-contact-link">800-222-3901</a> or <a href="mailto:info@desertharvest.com" class="dh-faq__ai-contact-link">info@desertharvest.com</a>.</div>'
        : '';

      // Product cards
      var productsHtml = '';
      if (data.product_links && data.product_links.length) {
        productsHtml = '<div class="dh-faq__ai-products">' +
          data.product_links.map(function (p) {
            var handle = (p.url || '').split('/products/')[1] || '';
            handle = handle.split('?')[0].split('#')[0];
            var tagline = productTagline(handle);
            return '<a class="dh-faq__ai-product" href="' + escapeAttr(p.url) + '" data-handle="' + escapeAttr(handle) + '">' +
              '<span class="dh-faq__ai-product-image" aria-hidden="true">' +
                '<img class="dh-faq__ai-product-img" alt="" loading="lazy" width="72" height="72" />' +
              '</span>' +
              '<span class="dh-faq__ai-product-body">' +
                '<span class="dh-faq__ai-product-name">' + escapeHtml(p.title) + '</span>' +
                (tagline ? '<span class="dh-faq__ai-product-tagline">' + escapeHtml(tagline) + '</span>' : '') +
                '<span class="dh-faq__ai-product-meta">' +
                  '<span class="dh-faq__ai-product-stars jdgm-widget jdgm-preview-badge" data-id="" data-template="preview"></span>' +
                  '<span class="dh-faq__ai-product-price"></span>' +
                '</span>' +
              '</span>' +
              '<span class="dh-faq__ai-product-arrow" aria-hidden="true">\u2192</span>' +
            '</a>';
          }).join('') +
        '</div>';
      }

      // Related FAQs — for verified tier, skip the primary match (its body is already shown)
      var relatedMatches = data.matched || [];
      if (isVerified && relatedMatches.length > 1) {
        relatedMatches = relatedMatches.slice(1);
      } else if (isVerified) {
        relatedMatches = [];
      }

      var matchesHtml = '';
      if (relatedMatches.length) {
        matchesHtml = '<div class="dh-faq__ai-matches">' +
          '<div class="dh-faq__ai-matches-label">Related FAQs</div>' +
          relatedMatches.map(function (m) {
            var href = m.url || '#' + m.id;
            return '<a class="dh-faq__ai-match" href="' + escapeAttr(href) + '">' +
              '<span class="dh-faq__ai-match-title">' + escapeHtml(m.title) + '</span>' +
            '</a>';
          }).join('') +
        '</div>';
      }

      var fallbackPhone = '<a href="tel:+18002223901" class="dh-faq__ai-contact-link">800-222-3901</a>';
      var fallbackNote = data.fallback_contact
        ? '<div class="dh-faq__ai-fallback-note">Still need help? Call our team at ' + fallbackPhone + ' or email <a class="dh-faq__ai-contact-link" href="mailto:info@desertharvest.com">info@desertharvest.com</a>.</div>'
        : '';

      var metaText = (data.cache_hit ? 'cached \u00b7 ' : '') + (latencyMs / 1000).toFixed(1) + 's \u00b7 based on our FAQ';

      aiContainer.innerHTML =
        '<div class="' + cardClass + '" role="region" aria-label="' + (isVerified ? 'FAQ answer' : 'AI-suggested answer') + '">' +
          '<div class="dh-faq__ai-header">' +
            '<span class="' + badgeClass + '">' + badgeText + '</span>' +
            '<span class="dh-faq__ai-muted">' + metaText + '</span>' +
          '</div>' +
          aiCaveat +
          '<p class="dh-faq__ai-summary">' + escapeHtml(summaryContent) + '</p>' +
          matchesHtml +
          productsHtml +
          fallbackNote +
          '<div class="dh-faq__ai-disclaimer">' + escapeHtml(data.disclaimer || '') + '</div>' +
          '<div class="dh-faq__ai-feedback" data-query-id="' + escapeAttr(data.query_id || '') + '">' +
            '<span class="dh-faq__ai-feedback-label">Was this helpful?</span>' +
            '<button type="button" class="dh-faq__ai-thumb" data-thumb="up" aria-label="Yes, helpful">\ud83d\udc4d</button>' +
            '<button type="button" class="dh-faq__ai-thumb" data-thumb="down" aria-label="No, not helpful">\ud83d\udc4e</button>' +
            '<span class="dh-faq__ai-feedback-thanks" hidden>Thanks \u2014 logged.</span>' +
          '</div>' +
        '</div>';
      wireFeedback();
      hydrateProductCards();
    }

    function hydrateProductCards() {
      var cards = aiContainer.querySelectorAll('.dh-faq__ai-product[data-handle]');
      if (!cards.length) return;
      var filled = 0;
      cards.forEach(function (card) {
        var handle = card.getAttribute('data-handle');
        if (!handle) return;
        fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            if (!p) return;
            var imgEl = card.querySelector('.dh-faq__ai-product-img');
            var priceEl = card.querySelector('.dh-faq__ai-product-price');
            var starsEl = card.querySelector('.dh-faq__ai-product-stars');

            // Image — use a proper <img> tag. Shopify supports ?width= to
            // serve a resized variant without touching filename.
            var rawImg = p.featured_image || (p.images && p.images[0]);
            if (rawImg && imgEl) {
              var src = typeof rawImg === 'string' ? rawImg : (rawImg.src || '');
              if (src.indexOf('//') === 0) src = 'https:' + src;
              var sep = src.indexOf('?') > -1 ? '&' : '?';
              imgEl.src = src + sep + 'width=160';
              imgEl.alt = p.title || '';
            }

            if (priceEl && typeof p.price === 'number') {
              priceEl.textContent = '$' + (p.price / 100).toFixed(2);
            }

            if (starsEl && p.id) {
              starsEl.setAttribute('data-id', String(p.id));
            }
            filled++;
            if (filled === cards.length) triggerJudgeMe();
          })
          .catch(function () {});
      });
    }

    function triggerJudgeMe() {
      // Judge.me reads data-id from widgets and renders the badge.
      // Retry a few times in case jdgm is still loading (race on first query).
      var attempts = 0;
      (function tryIt() {
        if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
          try { window.jdgm.customizeBadges(); } catch (e) {}
          return;
        }
        if (attempts++ < 15) setTimeout(tryIt, 250);
      })();
    }

    // Tagline dictionary — matches the mega menu case statement.
    // Keep in sync with snippets/mega-menu.liquid :: case gp.handle.
    var TAGLINES = {
      'super-strength-aloe-vera-90-capsules': 'IC/BPS bladder comfort, 200:1 strength',
      'ssav-super-strength-aloe-vera-180-capsules': 'IC/BPS bladder comfort, 200:1 strength',
      'quercetin-90-capsules': 'Natural mast cell support',
      'calcium-glycerophosphate-120-capsules': 'Dietary acid buffer — gentle on bladder',
      'heathers_uti_defense': 'Botanical urinary tract support',
      'ezmagic-therawands': 'At-home pelvic floor support',
      'lunation-period-and-bladder-leak-underwear': 'Bladder leak confidence, all day',
      'human-bioidentical-lactoferrin-aloe-vera-for-menopause-support-desert-harvest': 'Menopause iron & energy support',
      'collagen-90-capsules': 'Skin, bone & joint support',
      'aloe-glide': 'FDA-cleared intimate moisturizer',
      'vulva-body-balm': 'Daily comfort for sensitive vulvar skin',
      'aloe-vera-gele': 'Multi-use skin comfort gel',
      'releveum-skin-repair-cream': '4% lidocaine — targeted comfort',
      'aloe-fresh-personal-towelettes': 'On-the-go intimate freshness',
      'aloe-renew': 'For scars, stretch marks & skin renewal',
      'aloe-rebalance-feminine-wash': 'pH-balanced intimate care',
      'bum-bumz': 'Gentle posterior comfort cream',
      'b-complex-without-b6-90-capsules': 'Energy & nerve support — without B6',
      'multi-vitamin-low-acid-90-capsules': 'Low-acid daily multivitamin',
      'magnesium-160-capsules': 'Muscle relaxation & calm',
      'calcium-vitamin-d3-90-capsules': 'Bone & immune support',
      'buffered-vitamin-c-90-capsules': 'Buffered immune support — non-acidic',
      'vitamin-d3-1000-iu-90-capsules': 'Immune & bone support — 1000 IU',
      'probiotic-1-billion-cfu-90-capsules': 'Gut & digestive balance',
      'lions-mane-mushroom-supplement-90-capsules': 'Focus & cognitive clarity',
      'turkey-tail-mushroom-supplement-90-capsules': 'Prebiotic immune support',
      'z3nleaf': 'Trusted calming blend, 60 caps',
      'wunderlich-shampoo': 'Gentle aloe scalp care',
      'bladder-support-starter-bundle-aloe-180-quercetin-calcium': 'Bladder comfort starter kit',
      'pelvic-pain-bundle': 'Pelvic comfort starter kit',
      'pelvic-support-bundle': 'Daily intimate comfort bundle',
      'sexual-health-bundle-aloe-glide-8oz-personal-wipes-36-pack': 'Intimate care essentials',
      'stressed-bladder-bundle-ssav-180-zenleaf': 'Bladder comfort + calm bundle',
      'nutrition-bundle-b-complex-collagen-multi-vitamin': 'Daily nutrition essentials',
      'wellness-bundle-vitamin-c-90-quercetin-90-vitamin-d3-90': 'Immune support trio',
      'desert-harvest-dozen-variety': 'Try all three — 12 or 36 packets',
      'desert-harvest-dozen-glide': 'Aloe Glide travel packets — 12 or 36',
      'desert-harvest-dozen-releveum': 'Reléveum travel packets — 12 or 36',
      'desert-harvest-dozen-gele': 'Aloe Vera Gelé travel packets — 12 or 36',
      'lunation-laundry-bag': 'Reusable mesh travel & wash bag',
    };
    function productTagline(handle) {
      return TAGLINES[handle] || '';
    }

    function wireFeedback() {
      var buttons = aiContainer.querySelectorAll('.dh-faq__ai-thumb');
      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var wrap = btn.closest('.dh-faq__ai-feedback');
          if (!wrap) return;
          var queryId = wrap.getAttribute('data-query-id');
          if (!queryId) return;
          var thumb = btn.getAttribute('data-thumb');
          fetch(aiEndpoint + '/api/feedback', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query_id: queryId, thumb: thumb }),
            credentials: 'omit'
          }).catch(function () {});
          buttons.forEach(function (b) { b.disabled = true; });
          btn.classList.add('dh-faq__ai-thumb--active');
          var thanks = wrap.querySelector('.dh-faq__ai-feedback-thanks');
          if (thanks) thanks.hidden = false;
        });
      });
    }

    function pullMatchedToTop(matched) {
      // Only apply the visual highlight class — no auto-open, no scroll-yank.
      // Auto-opening and scrollIntoView were causing the page to jump while
      // the user was still reading / typing.
      if (!matched || !matched.length) return;
      matched.forEach(function (m) {
        var el = document.getElementById('faq-' + (m.id.split('__').pop() || m.id)) ||
                 document.querySelector('[id*="' + m.id + '"]');
        if (!el) return;
        el.classList.add('dh-faq__item--ai-match');
      });
    }

    function escapeAttr(s) {
      return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  }
})();
